import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, Zap, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Payment success page — Stripe redirects here with ?session_id=cs_test_xxx.
 * Polls /api/payments/checkout/status/{session_id} every 2s until payment_status==='paid'
 * or 5 attempts (10s) elapse. Once paid, refreshes the auth user so the new tier /
 * credit lands in AuthContext immediately.
 */
export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "checking", details: null });
    const attemptsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", details: "Missing session id" });
            return;
        }
        let active = true;
        const MAX_ATTEMPTS = 5;
        const INTERVAL = 2000;

        const poll = async () => {
            if (!active) return;
            attemptsRef.current += 1;
            try {
                const res = await api.get(`/payments/checkout/status/${sessionId}`);
                const d = res.data;
                if (d.payment_status === "paid") {
                    setState({ status: "paid", details: d });
                    if (refresh) refresh();  // pull updated tier / credits into AuthContext
                    return;
                }
                if (d.status === "expired") {
                    setState({ status: "expired", details: d });
                    return;
                }
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    setState({ status: "pending_timeout", details: d });
                    return;
                }
                setTimeout(poll, INTERVAL);
            } catch (e) {
                setState({ status: "error", details: e?.response?.data?.detail || "Unable to verify payment" });
            }
        };
        poll();
        return () => { active = false; };
    }, [sessionId, refresh]);

    return (
        <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="payment-success-page">
            <div className="max-w-md w-full border border-zinc-800 bg-zinc-950 p-8 text-center">
                {state.status === "checking" && (
                    <>
                        <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
                        <h1 className="font-heading font-bold text-xl text-zinc-50">Verifying payment…</h1>
                        <p className="text-sm text-zinc-500 mt-2 font-mono">Talking to Stripe. This takes a few seconds.</p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" data-testid="success-icon" />
                        <h1 className="font-heading font-black text-2xl text-zinc-50 tracking-tight">Payment received.</h1>
                        {state.details?.grants === "pro_30day" && (
                            <div className="mt-4 inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 px-4 py-2 font-mono text-xs uppercase tracking-wider text-amber-300">
                                <Zap className="w-4 h-4" /> Pro activated · 30 days · 2/day
                            </div>
                        )}
                        {state.details?.grants === "pro_plus_30day" && (
                            <div className="mt-4 inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 px-4 py-2 font-mono text-xs uppercase tracking-wider text-amber-300">
                                <Zap className="w-4 h-4" /> Pro+ activated · 30 days · 5/day
                            </div>
                        )}
                        {state.details?.grants === "pro_elite_30day" && (
                            <div className="mt-4 inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 px-4 py-2 font-mono text-xs uppercase tracking-wider text-amber-300">
                                <Zap className="w-4 h-4" /> Elite activated · 30 days · unlimited
                            </div>
                        )}
                        {(state.details?.grants === "research_express_credit" || state.details?.grants === "one_off_credit") && (
                            <div className="mt-4 inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 px-4 py-2 font-mono text-xs uppercase tracking-wider text-amber-300">
                                <Zap className="w-4 h-4" /> +1 Express research (Fast Lane)
                            </div>
                        )}
                        {state.details?.grants === "research_standard_credit" && (
                            <div className="mt-4 inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 font-mono text-xs uppercase tracking-wider text-emerald-300">
                                <Sparkles className="w-4 h-4" /> +1 Standard research (priority queue)
                            </div>
                        )}
                        <p className="text-sm text-zinc-400 mt-5 leading-relaxed">
                            {state.details?.grants?.startsWith("pro_") || state.details?.grants === "pro_30day"
                                ? "Your next request fires on the Fast Lane — instant analyst within 60 seconds. Use them up before they reset at midnight UTC."
                                : state.details?.grants === "research_standard_credit"
                                ? "Your next research request will use this credit — priority-queue delivery, ~50% faster than the free wait. The credit never expires."
                                : "Your next research request automatically uses this credit — instant Fast Lane delivery. The credit never expires."}
                        </p>
                        <button
                            onClick={() => navigate("/app/requests")}
                            className="mt-6 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm uppercase tracking-wider px-6 py-3 w-full"
                            data-testid="continue-btn"
                        >
                            Continue to Requests →
                        </button>
                    </>
                )}
                {state.status === "pending_timeout" && (
                    <>
                        <Loader2 className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
                        <h1 className="font-heading font-bold text-xl text-zinc-50">Still processing</h1>
                        <p className="text-sm text-zinc-500 mt-3 font-mono leading-relaxed">
                            Stripe is taking longer than usual. You'll get a confirmation email the moment it clears,
                            and your account will update automatically.
                        </p>
                    </>
                )}
                {state.status === "expired" && (
                    <>
                        <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                        <h1 className="font-heading font-bold text-xl text-zinc-50">Session expired</h1>
                        <p className="text-sm text-zinc-500 mt-3 font-mono">Your checkout session timed out. No charge was made.</p>
                        <button
                            onClick={() => navigate("/app/upgrade")}
                            className="mt-6 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold text-sm uppercase tracking-wider px-6 py-3 w-full"
                        >
                            Try again
                        </button>
                    </>
                )}
                {state.status === "error" && (
                    <>
                        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                        <h1 className="font-heading font-bold text-xl text-zinc-50">Something went wrong</h1>
                        <p className="text-sm text-zinc-500 mt-3 font-mono">{state.details}</p>
                        <button
                            onClick={() => navigate("/app/upgrade")}
                            className="mt-6 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold text-sm uppercase tracking-wider px-6 py-3 w-full"
                        >
                            Back to upgrade
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
